import { createBrowserRouter } from "react-router";

import { hrRoutes } from "@/app/routes/hrRoutes";
import NotFoundPage from "@/app/routes/NotFoundPage";
import { invitationRoute, peopleRoutes } from "@/app/routes/peopleRoutes";
import { RouteError } from "@/app/routes/RouteError";
import { RequireCapability } from "@/app/session/RequireCapability";
import { FullScreenLoader, RequireSession } from "@/app/session/RequireSession";
import { AppShell } from "@/app/shell/AppShell";
import { portalRoute } from "@/portal/routes";

/* Pages load lazily so the sign-in screen does not ship the whole app. */
export const router = createBrowserRouter([
  {
    path: "/sign-in",
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({ Component: (await import("@/features/auth/routes/SignInPage")).default }),
  },
  {
    path: "/forgot-password",
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/auth/routes/ForgotPasswordPage")).default,
    }),
  },
  {
    path: "/forgot-password/code",
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/auth/routes/ResetCodePage")).default,
    }),
  },
  {
    path: "/forgot-password/new",
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/auth/routes/NewPasswordPage")).default,
    }),
  },
  invitationRoute,
  portalRoute,
  {
    // A quote's public page: the client opens it from a link, with no account.
    path: "/q/:token",
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/billing/routes/PublicQuotePage")).default,
    }),
  },
  {
    // A job offer's public page: the candidate opens it from a link, with no account.
    path: "/offer/:token",
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullScreenLoader />,
    lazy: async () => ({
      Component: (await import("@/features/recruitment/routes/PublicOfferPage")).default,
    }),
  },
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullScreenLoader />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("@/features/home/routes/HomePage")).default,
            }),
          },
          {
            element: <RequireCapability permission="leads.manage" />,
            children: [
              {
                path: "leads",
                lazy: async () => ({
                  Component: (await import("@/features/leads/routes/LeadsPage")).default,
                }),
              },
            ],
          },
          {
            element: <RequireCapability permission="tickets.view" />,
            children: [
              {
                path: "tickets",
                lazy: async () => ({
                  Component: (await import("@/features/tickets/routes/TicketsPage")).default,
                }),
              },
              {
                path: "tickets/:ticketId",
                lazy: async () => ({
                  Component: (await import("@/features/tickets/routes/TicketsPage")).default,
                }),
              },
            ],
          },
          {
            path: "my-work",
            lazy: async () => ({
              Component: (await import("@/features/my-work/routes/MyWorkPage")).default,
            }),
          },
          {
            path: "projects",
            lazy: async () => ({
              Component: (await import("@/features/projects/routes/ProjectsPage")).default,
            }),
          },
          {
            element: <RequireCapability permission="projects.view_all" />,
            children: [
              {
                path: "tasks",
                lazy: async () => ({
                  Component: (await import("@/features/tasks/routes/AllTasksBoard")).default,
                }),
              },
            ],
          },
          {
            path: "projects/:key",
            lazy: async () => ({
              Component: (await import("@/features/projects/routes/ProjectLayout")).default,
            }),
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import("@/features/tasks/routes/BoardView")).default,
                }),
              },
              {
                path: "list",
                lazy: async () => ({
                  Component: (await import("@/features/tasks/routes/ListView")).default,
                }),
              },
              {
                path: "plan",
                lazy: async () => ({
                  Component: (await import("@/features/planning/routes/PlanView")).default,
                }),
              },
              {
                path: "calendar",
                lazy: async () => ({
                  Component: (await import("@/features/calendar/routes/ProjectCalendarView"))
                    .default,
                }),
              },
              {
                path: "overview",
                lazy: async () => ({
                  Component: (await import("@/features/projects/routes/OverviewView")).default,
                }),
              },
              {
                path: "members",
                lazy: async () => ({
                  Component: (await import("@/features/projects/routes/MembersView")).default,
                }),
              },
              {
                path: "settings",
                lazy: async () => ({
                  Component: (await import("@/features/projects/routes/SettingsView")).default,
                }),
              },
              {
                path: "files",
                lazy: async () => ({
                  Component: (await import("@/features/files/routes/ProjectFilesView")).default,
                }),
              },
              {
                path: "approvals",
                lazy: async () => ({
                  Component: (await import("@/features/approvals/routes/ProjectApprovalsView"))
                    .default,
                }),
              },
            ],
          },
          {
            path: "time",
            lazy: async () => ({
              Component: (await import("@/features/time/routes/TimesheetPage")).default,
            }),
          },
          {
            path: "reports/time",
            lazy: async () => ({
              Component: (await import("@/features/time/routes/TimeReportsPage")).default,
            }),
          },
          {
            element: <RequireCapability permission="billing.view" />,
            children: [
              {
                path: "billing",
                lazy: async () => {
                  const page = await import("@/features/billing/routes/BillingPage");
                  return { Component: page.BillingIndexRedirect };
                },
              },
              {
                path: "billing/quotes",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingPage")).default,
                }),
              },
              {
                path: "billing/contracts",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingPage")).default,
                }),
              },
              {
                path: "billing/invoices",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingPage")).default,
                }),
              },
              {
                path: "billing/payments",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingPage")).default,
                }),
              },
              {
                path: "billing/expenses",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingPage")).default,
                }),
              },
              {
                path: "billing/reports",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/BillingReportsPage")).default,
                }),
              },
              {
                path: "billing/quotes/new",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/NewQuotePage")).default,
                }),
              },
              {
                path: "billing/quotes/:id",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/QuotePage")).default,
                }),
              },
              {
                path: "billing/contracts/new",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/NewContractPage")).default,
                }),
              },
              {
                path: "billing/contracts/:id",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/ContractPage")).default,
                }),
              },
              {
                path: "billing/invoices/new",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/NewInvoicePage")).default,
                }),
              },
              {
                path: "billing/invoices/:id",
                lazy: async () => ({
                  Component: (await import("@/features/billing/routes/InvoicePage")).default,
                }),
              },
            ],
          },
          ...peopleRoutes,
          ...hrRoutes,
          // Static: the error page shows it too, so it is in the main bundle anyway.
          { path: "*", Component: NotFoundPage },
        ],
      },
    ],
  },
]);
